import { useContext, useMemo } from "react";

import { UserContext } from "@context/user";
import useContractDocument from "@panel/shared/contract/hooks/use-contract-document";
import useContractReview from "@panel/shared/contract/hooks/use-contract-review";
import { PUBLISHER_CONTRACT_STATUS, REVIEW_HINT } from "@panel/shared/contract/misc/constants";
import usePublisherRouteGuard from "../../use-publisher-route-guard";
import usePublisherContracts from "./use-publisher-contracts";
import { PUBLISHER_ENDPOINTS } from "@constants/endpoints";
import { isWaitingOnBrand } from "../misc/helpers";

// Container hook for the publisher Contracts page: it holds every piece of
// state the screen needs and hands the view a flat, render-ready object, so
// index.jsx and everything under components/ stay free of hooks.
const usePublisherContractsPage = () => {
    const { publisherId } = usePublisherRouteGuard();
    const { publishers } = useContext(UserContext);
    const brandName = useMemo(
        () => publishers.find((publisher) => publisher.id === publisherId)?.name || "",
        [publishers, publisherId]
    );

    const {
        loading,
        accepting,
        contracts,
        awaitingCount,
        acknowledgeContract
    } = usePublisherContracts(publisherId);

    // The payload carries no brand or file name of its own — the switcher
    // knows which account is on screen, so the name is attached here.
    const rows = useMemo(
        () => contracts.map((contract) => ({
            ...contract,
            brandName,
            name: brandName ? `${brandName} contract` : "Contract"
        })),
        [contracts, brandName]
    );

    // The account has exactly one contract, so the table is either empty or a
    // single row and the review modal always points at that row.
    const contract = rows[0] || null;
    const documentPath = publisherId ? PUBLISHER_ENDPOINTS.CONTRACT_DOCUMENT(publisherId) : "";

    const { pendingPath, downloadDocument } = useContractDocument("affiliate");

    // The gating — the document has to have been shown before the signature
    // is reachable — is the same as the brand's, so it lives in the shared
    // hook.
    const { openReview, modal } = useContractReview({
        contract,
        base         : "affiliate",
        documentPath,
        onAcknowledge: acknowledgeContract,
        readOnly     : contract?.status === PUBLISHER_CONTRACT_STATUS.SIGNED,
        blockedHint  : isWaitingOnBrand(contract) ? REVIEW_HINT.WAITING_ON_BRAND : ""
    });

    return {
        publisherId,
        loading,
        awaitingCount,
        table: {
            contracts  : rows,
            loading,
            onReview   : openReview,
            downloading: pendingPath === documentPath,
            onDownload : (row) => downloadDocument(documentPath, row.name)
        },
        reviewModal: {
            ...modal,
            accepting,
            subtitle: brandName
        }
    };
};

export default usePublisherContractsPage;
